<!--

  @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
  @license AGPL-3.0

  This code is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.

  Any use of the work other than as authorized under this license or copyright law is prohibited.

-->

<template>
  <li
    class="category-browser-contact-item contact"
    :aria-label="ariaLabel"
    :data-cid="data.cid"
    data-detail-popup="contact"
  >
    <div ref="avatarPlaceholder" class="avatar" aria-hidden="true"></div>
    <div class="info-container">
      <div
        :title="nameTooltip"
        class="fullname"
        aria-hidden="true"
        v-html="renderFullname()"
      ></div>
      <div class="description text-gray ellipsis" aria-hidden="true" :title="description">
        {{ description }}
      </div>
      <BadgeList :limit="3" :collection="categories"/>
    </div>
  </li>
</template>

<script setup>
import $ from '@/jquery'
import * as contactsUtil from '@/io.ox/contacts/util'
import * as coreUtil from '@/io.ox/core/util'
import BadgeList from '@/io.ox/core/categories/browser/badge-list.vue'
import contactsAPI from '@/io.ox/contacts/api'
import { ref, onMounted } from 'vue'
import svg from '@/io.ox/core/svg'
import gt from 'gettext'

const { data } = defineProps(['data'])
const description = contactsUtil.getSummaryBusiness(data) || contactsUtil.getMail(data)
const categories = (data.categories || '').split(',')
const ariaLabel = ref('')
const nameTooltip = ref('')

function renderFullname () {
  const tempNode = $('<div>')
  let name = contactsUtil.getFullName(data)
  if (name) {
    // use html output
    coreUtil.renderPersonalName({ $el: tempNode, html: contactsUtil.getFullName(data, true) }, data)
  } else {
    name = contactsUtil.getFullName(data) || data.yomiLastName || data.yomiFirstName || data.display_name || contactsUtil.getMail(data)
    coreUtil.renderPersonalName({ $el: tempNode, name }, data)
  }
  name = name.trim()
  nameTooltip.value = name === '' ? gt('Empty name and description found.') : name
  ariaLabel.value = name || description || gt('Empty name and description found.')
  if (name === '' && description === '') return gt('Empty name and description found.')
  return tempNode.html()
}

// render elements that are not part of the template.
const avatarPlaceholder = ref()
onMounted(() => {
  const url = contactsAPI.getContactPhotoUrl(data, 48)
  if (url) return $(avatarPlaceholder.value).css('background-image', 'url(' + url + ')')
  const initials = contactsUtil.getInitials(data)
  const initialsColor = contactsUtil.getInitialsColor(initials)
  $(avatarPlaceholder.value)
    .css('background-image', '')
    .addClass('empty initials ' + initialsColor)
    .append(svg.circleAvatar(initials))
})

</script>

<style>
.category-browser-contact-item {
  height: 4.5rem;
  cursor: pointer;
  padding: 8px 16px 8px 0;
  display: flex;

  .avatar {
    width: 32px;
    height: 32px;
    margin-right: 2px;
  }

  .info-container {
    width: calc(100% - 48px)
  }
  .fullname { font-weight: bold }
  .fullname, .description {
    height: 16px
  }
  .categories-badges .category-view {
    margin-top: 4px;
    line-height: 18px;
    height: 18px;
    border-radius: 6px;
    padding-inline: 4px;
  }
  &:focus .description { color: var(--selected-focus);}
}
</style>
